import { useState } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { Checkbox, NumberStepper, Select, Text } from '@kula-ai/components';
import { KModal } from 'ui-library/components';
import { useShowToast } from '@/hooks/useShowToast';
import { useConsentLifecycleAction } from './api';
import { CONSENT_ACTION_SUCCESS, consentErrorMessage } from './consentStatus';
import { UNIT_OPTIONS, buildExtendPayload } from './extendConsentValidity';
import type { ConsentLifecycleAction, ExtendUnit } from './api';
import type { ConsentTarget } from './types';

interface ExtendConsentModalProps {
  target: ConsentTarget;
  /**
   * Which grant endpoint carries the chosen validity: `extend` for an active
   * consent ("Extend consent manually"), `mark_granted` for a fresh one
   * ("Mark consent manually"). Both send the same {value,unit} / {no_expiry} body.
   */
  action?: ConsentLifecycleAction;
  /** Matches the menu label ("Mark consent manually"). */
  title: string;
  isOpen: boolean;
  onClose: () => void;
  invalidateKeys?: unknown[][];
  onSuccess?: () => void;
}

export const ExtendConsentModal = ({
  target,
  action = 'extend',
  title,
  isOpen,
  onClose,
  invalidateKeys,
  onSuccess,
}: ExtendConsentModalProps) => {
  const showToast = useShowToast();
  const lifecycle = useConsentLifecycleAction({ invalidateKeys });
  const [value, setValue] = useState(6);
  const [unit, setUnit] = useState<ExtendUnit>('months');
  const [noExpiry, setNoExpiry] = useState(false);

  const handleSave = () =>
    lifecycle.mutate(
      {
        action,
        target,
        extend: buildExtendPayload(value, unit, noExpiry),
      },
      {
        onSuccess: () => {
          showToast({
            title: CONSENT_ACTION_SUCCESS[action],
            status: 'success',
          });
          onSuccess?.();
          onClose();
        },
        onError: (err) =>
          showToast({ title: consentErrorMessage(err), status: 'error' }),
      }
    );

  return (
    <KModal
      size="md"
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      ctaLabel="Save"
      ctaAction={handleSave}
      isCtaLoading={lifecycle.isPending}
      loadingText="Saving"
    >
      {/* 12px between the field group and the Never-expires checkbox. */}
      <Flex flexDir="column" gap="12px" pb="8px">
        {/* 6px between the label and its stepper/unit row. */}
        <Flex flexDir="column" gap="6px">
          {/* Fresh consent reads "valid for"; an active one is extended "by". */}
          <Text variant="h4">
            {action === 'extend' ? 'Extend consent by' : 'Consent valid for'}
          </Text>
          <Flex align="center" gap="8px">
            <Box flex={1}>
              <NumberStepper
                // +1 / −1 controls in secondary-700 (! beats the button's own
                // descendant styling).
                buttonClassName="!text-secondary-700 [&_span]:!text-secondary-700"
                min={1}
                max={999}
                value={value}
                onChange={setValue}
                disabled={noExpiry}
              />
            </Box>
            <Box flex={1}>
              <Select
                options={UNIT_OPTIONS}
                value={unit}
                onChange={(v: unknown) => setUnit(v as ExtendUnit)}
                disabled={noExpiry}
                // Radix focus-trap absorbs body-portaled menus — keep the menu in-flow.
                menuPortalTarget={null}
                reactSelectProps={{ menuPosition: 'fixed' }}
              />
            </Box>
          </Flex>
        </Flex>
        <Checkbox
          checked={noExpiry}
          // kula Checkbox extends InputHTMLAttributes — only native onChange fires.
          onChange={(e) => setNoExpiry(e.target.checked)}
          label="Never expires"
        />
      </Flex>
    </KModal>
  );
};
